<template>
  <v-navigation-drawer
    v-model="drawer"
    temporary
    right
    width="450"
    height="100%"
    app
  >
    <v-card flat>
      <v-card-title> Add Page </v-card-title>
      <v-card-text>
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-select
            v-if="type == 'page'"
            v-model="value.contentPageTypeId"
            :items="content_page_types"
            item-text="name"
            item-value="id"
            label="Content Page Type"
            outlined
            clearable
            :rules="[$rules.required]"
          />
          <!-- A blog post has no lookup row behind it: the idea is the whole input, and the AI derives the
               title, url and the industry/topic/category it files under from what is typed here. -->
          <template v-if="isBlog">
            <v-textarea
              v-model="value.idea"
              label="What should the post be about?"
              placeholder="A checklist realtors can follow for print marketing around a spring open house"
              rows="4"
              auto-grow
              outlined
              :rules="[$rules.required]"
            />
            <v-autocomplete
              v-model="value.authorUserInfoId"
              :items="content_authors"
              item-text="name"
              item-value="id"
              label="Author"
              hint="Leave blank to use your own byline"
              persistent-hint
              class="mb-4"
              outlined
              clearable
            />
          </template>
          <v-autocomplete
            v-if="showCategories"
            v-model="value.categoryId"
            :items="categories"
            item-text="name"
            item-value="id"
            label="Category"
            outlined
            clearable
            :rules="[$rules.required]"
          />
          <v-autocomplete
            v-if="type == 'page' && value.categoryId && [1, 2].includes(value.contentPageTypeId)"
            v-model="value.coreProductIds"
            :items="products"
            item-text="name"
            item-value="id"
            label="Core Products"
            outlined
            clearable
            multiple
          />
          <v-autocomplete
            v-if="showIndustry"
            v-model="value.industryId"
            :items="industries"
            item-text="name"
            item-value="id"
            label="Industry"
            :disabled="!!value.topicId"
            outlined
            clearable
          />
          <v-autocomplete
            v-if="showTopic"
            v-model="value.topicId"
            :items="selectableTopics"
            item-text="name"
            item-value="id"
            label="Topic"
            :disabled="!!value.industryId"
            outlined
            clearable
          />
          <template v-if="showDesignGroup">
            <v-autocomplete
              v-model="value.designGroupId"
              :items="design_groups"
              item-text="name"
              item-value="id"
              label="Design Group"
              class="mb-4"
              outlined
              clearable
              :rules="[$rules.required]"
              :messages="[design_group ? `${design_group.published_templates} Templates` : '']"
            />
            <div v-if="design_group"></div>
          </template>
          <v-text-field
            v-if="showName"
            v-model="value.name"
            :label="name"
            outlined
            clearable
            :rules="[$rules.required]"
          />
          <v-text-field
            v-if="showNamePlural"
            v-model="value.namePlural"
            label="Name Plural"
            outlined
            clearable
            :rules="[$rules.required]"
          />
          <v-text-field
            v-if="showUrlSegment"
            v-model="value.urlSegment"
            label="Url Segment"
            outlined
            clearable
            :rules="[$rules.required]"
          />
          <template v-if="type == 'page' && value.contentPageTypeId == 4">
            <v-text-field
              v-model="value.exampleUrl"
              label="Example Url"
              outlined
              clearable
              :rules="[$rules.required]"
            />
            <v-text-field
              v-model="value.prompt"
              label="Prompt"
              outlined
              clearable
              :rules="[$rules.required]"
            />
          </template>
        </v-form>
      </v-card-text>
    </v-card>
    <template #append>
      <div class="px-3 py-5">
        <v-btn
          color="primary"
          block
          large
          :loading="saving"
          @click="save"
        >
          Add
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
  name: 'AddPage',
  components: {},
  data() {
    return {
      type: '',
      valid: false,
      drawer: false,
      saving: false,
      value: {}
    };
  },
  computed: {
    products() {
      if (!this.value.categoryId) {
        return [];
      }
      return this.standard_products.filter(
        (x) => x.category.id === this.value.categoryId && x.active
      );
    },
    industry() {
      return this.industries.find((x) => x.id === this.value.industryId);
    },
    topic() {
      // Resolved off the FULL list so a page already filed under a retired topic still renders its name.
      return this.topics.find((x) => x.id === this.value.topicId);
    },
    // The picker only offers live topics — but keeps the currently-selected one even if it's retired, so
    // opening an existing page doesn't silently blank its topic and save the change back.
    selectableTopics() {
      const active = this.topics.filter((x) => x.active);
      const current = this.topic;
      return current && !current.active ? [current].concat(active) : active;
    },
    design_group() {
      return this.design_groups.find((x) => x.id === this.value.designGroupId);
    },
    isBlog() {
      return this.type === 'page' && this.value.contentPageTypeId === 6;
    },
    showCategories() {
      return this.type !== 'for' && ![3, 4, 5, 6].includes(this.value.contentPageTypeId);
    },
    showIndustry() {
      return this.type === 'page' && this.value.contentPageTypeId === 3;
    },
    showTopic() {
      return this.type === 'page' && this.value.contentPageTypeId === 3;
    },
    showDesignGroup() {
      return this.value.contentPageTypeId === 5;
    },
    showName() {
      return (
        this.type === 'page' &&
        !this.isBlog &&
        ((this.value.coreProductIds || []).length || ![1, 2].includes(this.value.contentPageTypeId))
      );
    },
    showNamePlural() {
      return this.type === 'page' && this.showName;
    },
    showUrlSegment() {
      return this.showName && this.type === 'page';
    },
    name() {
      return 'Name';
    },
    ...mapGetters({
      categories: 'lookup/categories',
      standard_products: 'lookup/standard_products',
      industries: 'lookup/industries',
      topics: 'lookup/topics',
      content_page_types: 'lookup/content_page_types',
      content_authors: 'lookup/content_authors',
      design_groups: 'lookup/design_group_non_admin'
    })
  },

  watch: {
    'value.contentPageTypeId'(val, oldVal) {
      if (val !== oldVal) {
        this.value.name = '';
        this.value.namePlural = '';
        this.value.urlSegment = '';
      }
      // Only the blog branch needs the author list, so don't pay for it until Blog is chosen.
      if (this.isBlog) {
        this.$store.dispatch('lookup/getContentAuthors').catch((error) => {
          this.logError(error);
        });
      }
    },
    'value.topicId'(val, oldVal) {
      if (val !== oldVal && !!val) {
        this.value.name = this.topic.simple_name;
        // No "+ s" — a topic/industry/design group name has no plural form, and the value ends up
        // verbatim in the breadcrumb label and the {{*_plural}} copy tokens.
        this.value.namePlural = this.topic.simple_name;
        this.value.urlSegment = this.topic.url_segment;
      }
    },
    'value.industryId'(val, oldVal) {
      if (val !== oldVal && !!val) {
        this.value.name = this.industry.name;
        this.value.namePlural = this.industry.name;
        this.value.urlSegment = this.industry.url_segment;
      }
    },
    'value.designGroupId'(val, oldVal) {
      if (val !== oldVal && !!val) {
        this.value.name = this.design_group.simple_name;
        this.value.namePlural = this.design_group.simple_name;
        this.value.urlSegment = this.design_group.url_segment;
      }
    }
  },

  mounted() {
    this.$bus.$off(this.$enums.EVENT_BUS_EVENTS.PAGE_ADD_SHOW);
    this.$bus.$on(this.$enums.EVENT_BUS_EVENTS.PAGE_ADD_SHOW, (data) => {
      this.type = data.type;
      this.drawer = true;
      this.value = {};
    });
  },
  methods: {
    save() {
      if (this.$refs.form.validate()) {
        let action = '';
        switch (this.type) {
          case 'page':
            action = 'content/addPage';
            break;
        }
        this.saving = true;
        this.$store
          .dispatch(action, this.value)
          .then(() => {
            this.drawer = false;
            this.$bus.$emit(this.$enums.EVENT_BUS_EVENTS.PAGE_ADD_SAVED);
          })
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>
